import { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';
import Layout from '../components/Layout';

const AGENT_NAMES: Record<string, string> = {
  factsagent: 'FactsAgent',
  topicalmap: 'Topical Map V1',
  topicalmapv2: 'Topical Map V2',
  cppages: 'CP Pages',
  content_writer: 'Blog Posts',
  contentframeworkv2: 'Content Framework V2',
  contentframework: 'Content Framework V1',
  cpcustom: 'CF Custom',
  cpimages: 'CP Images',
  briefcontent: 'Bulk Brief',
  liveupdate: 'WP Live Update',
  payloadagent: 'Payload Agent',
  imagegenerator: 'Image Generator',
  socialmedia: 'Social Media',
  gdriveeditor: 'Google Drive',
  articleupdate: 'Article Update',
  icpagent: 'ICP Agent',
  brandbrain: 'Brand Brain',
  llmvisibility: 'LLM Visibility',
  intentvolume: 'Intent Volume',
  serviceareapages: 'Service Area Pages',
  contentgen: 'Content Gen',
  bahaagents: 'Baha Agents',
  seotools: 'SEO Tools',
  sop_topical_map: 'SOP Topical Map',
  sop_orchestrator: 'SOP Orchestrator',
  sop_entity: 'SOP Entity',
  sop_crawl: 'SOP Crawl',
  sop_kg: 'SOP Knowledge Graph',
  sop_source_context: 'SOP Source Context',
};

function agentName(st: string): string {
  if (!st) return 'Unknown';
  return AGENT_NAMES[st] || st;
}

interface AgentEvent {
  agent: string;
  message: string;
  project_id: number;
  job_id: number | null;
  timestamp: number;
  level: string;
  detail?: {
    type?: string;
    agent?: string;
    system_type?: string;
    job_id?: number;
    parent_agent?: string;
    [key: string]: any;
  };
}

interface AgentCallChain {
  job_id: number;
  project_id: number;
  chain: Array<{
    agent: string;
    timestamp: number;
    type: string;
  }>;
  started_at: number;
}

export default function AgentMonitorPage() {
  const [registeredAgents, setRegisteredAgents] = useState<any[]>([]);
  const [runningTasks, setRunningTasks] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [events, setEvents] = useState<AgentEvent[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [agentCallChains, setAgentCallChains] = useState<Map<number, AgentCallChain>>(new Map());
  const [expandedJobId, setExpandedJobId] = useState<number | null>(null);

  const eventSourceRef = useRef<EventSource | null>(null);
  const eventsEndRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    setError('');
    try {
      const [agentsRes, runningRes, statsRes] = await Promise.all([
        api.get('/events/agents'),
        api.get('/events/monitor/running'),
        api.get('/events/monitor/stats'),
      ]);
      setRegisteredAgents(agentsRes.data.agents || []);
      setRunningTasks(runningRes.data.tasks || []);
      setStats(statsRes.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load agent data');
    }
    setLoading(false);
  };

  // Connect to SSE stream
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const apiUrl = window.location.origin;
    const eventSource = new EventSource(
      `${apiUrl}/api/events/monitor/stream?token=${token}&backfill=50`
    );

    eventSource.onopen = () => {
      console.log('SSE connected');
      setIsConnected(true);
    };

    eventSource.onmessage = (e) => {
      if (e.data.startsWith(':')) return; // heartbeat
      try {
        const event: AgentEvent = JSON.parse(e.data);
        setEvents((prev) => [...prev.slice(-199), event]); // Keep last 200 events

        // Track agent call chains
        if (event.job_id) {
          setAgentCallChains((prev) => {
            const chains = new Map(prev);
            const existing = chains.get(event.job_id!);

            if (existing) {
              // Add to existing chain
              existing.chain.push({
                agent: event.agent,
                timestamp: event.timestamp,
                type: event.detail?.type || 'event'
              });
            } else if ((event.detail?.type === 'dispatch' || event.agent === 'Gateway') && event.job_id !== null) {
              // Start new chain
              chains.set(event.job_id, {
                job_id: event.job_id,
                project_id: event.project_id,
                chain: [{
                  agent: event.agent,
                  timestamp: event.timestamp,
                  type: event.detail?.type || 'start'
                }],
                started_at: event.timestamp
              });
            }

            // Clean up completed chains (older than 5 minutes)
            const cutoff = Date.now() / 1000 - 300;
            Array.from(chains.entries()).forEach(([jobId, chain]) => {
              if (chain.started_at < cutoff && !runningTasks.some(t => t.job_id === jobId)) {
                chains.delete(jobId);
              }
            });

            return chains;
          });
        }

        // Auto-refresh running tasks when new events come in
        if (event.level === 'success' || event.level === 'error') {
          api.get('/events/monitor/running').then((res) => {
            setRunningTasks(res.data.tasks || []);
          });
        }
      } catch (err) {
        console.error('Failed to parse SSE event:', err);
      }
    };

    eventSource.onerror = () => {
      console.error('SSE disconnected');
      setIsConnected(false);
      eventSource.close();
      // Reconnect after 5 seconds
      setTimeout(() => {
        if (eventSourceRef.current === eventSource) {
          eventSourceRef.current = null;
        }
      }, 5000);
    };

    eventSourceRef.current = eventSource;

    return () => {
      eventSource.close();
      eventSourceRef.current = null;
    };
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    if (autoScroll && eventsEndRef.current) {
      eventsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [events, autoScroll]);

  // Refresh running tasks every 10s
  useEffect(() => {
    const interval = setInterval(() => {
      api.get('/events/monitor/running').then((res) => {
        setRunningTasks(res.data.tasks || []);
      });
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'error': return 'text-red-600 bg-red-50';
      case 'warning': return 'text-amber-600 bg-amber-50';
      case 'success': return 'text-green-600 bg-green-50';
      case 'info': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getAgentStatusColor = (systemType: string) => {
    const isRunning = runningTasks.some(t => t.agent === systemType);
    return isRunning ? 'bg-green-100 text-green-700 border-green-300' : 'bg-gray-100 text-gray-600 border-gray-300';
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Agent Monitor</h1>
              <p className="text-gray-500 text-sm mt-1">Real-time agent execution tracking across all projects</p>
            </div>
            <div className="flex items-center gap-3">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm ${
                isConnected ? 'bg-green-50 text-green-700 border-green-300' : 'bg-red-50 text-red-700 border-red-300'
              }`}>
                <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></span>
                {isConnected ? 'Connected' : 'Disconnected'}
              </div>
              <button
                onClick={loadInitialData}
                className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm hover:bg-brand-700"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>

        {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <span className="spinner mr-2"></span>
            <span className="text-gray-500">Loading...</span>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Stats Cards */}
            {stats && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <StatCard
                  label="Registered Agents"
                  value={stats.registered_agents?.length || 0}
                  color="brand"
                />
                <StatCard
                  label="Active Agents"
                  value={stats.active_agents?.length || 0}
                  color="green"
                />
                <StatCard
                  label="Running Now"
                  value={stats.running_now?.length || 0}
                  color="blue"
                />
                <StatCard
                  label="Total Events"
                  value={stats.total_events || 0}
                  color="purple"
                />
              </div>
            )}

            {/* Agent Call Chains */}
            {agentCallChains.size > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-base font-semibold text-gray-900">Live Agent Call Chains</h3>
                  <p className="text-xs text-gray-400 mt-0.5">Real-time agent execution flow (Gateway → Sub-agents)</p>
                </div>
                <div className="p-6 space-y-4 max-h-[400px] overflow-y-auto">
                  {Array.from(agentCallChains.values())
                    .sort((a, b) => b.started_at - a.started_at)
                    .map((chain) => (
                      <div key={chain.job_id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-gray-500">Job #{chain.job_id}</span>
                            <span className="text-xs text-gray-400">·</span>
                            <span className="text-xs text-gray-500">Project #{chain.project_id}</span>
                          </div>
                          <span className="text-xs text-gray-400">
                            {new Date(chain.started_at * 1000).toLocaleTimeString()}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {chain.chain.map((step, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              {idx > 0 && (
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              )}
                              <div className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                                step.agent === 'Gateway'
                                  ? 'bg-purple-100 text-purple-700 border border-purple-300'
                                  : runningTasks.some(t => t.job_id === chain.job_id && t.agent === step.agent)
                                  ? 'bg-green-100 text-green-700 border border-green-300'
                                  : 'bg-blue-100 text-blue-700 border border-blue-300'
                              }`}>
                                {agentName(step.agent)}
                                {runningTasks.some(t => t.job_id === chain.job_id && t.agent === step.agent) && (
                                  <span className="ml-1.5 inline-flex w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Running Tasks */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Currently Running Agents</h3>
                  <p className="text-xs text-gray-400 mt-0.5">Active agent executions across all projects</p>
                </div>
                <span className="text-sm font-medium text-gray-600">
                  {runningTasks.length} {runningTasks.length === 1 ? 'task' : 'tasks'}
                </span>
              </div>
              <div className="max-h-[300px] overflow-y-auto">
                {runningTasks.length > 0 ? (
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Agent</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Project</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Job #</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Started</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {runningTasks.map((task: any, i: number) => {
                        const jobEvents = events.filter(e => e.job_id === task.job_id);
                        const isExpanded = expandedJobId === task.job_id;

                        return (
                          <>
                            <tr
                              key={i}
                              onClick={() => setExpandedJobId(isExpanded ? null : task.job_id)}
                              className="hover:bg-gray-50 cursor-pointer"
                            >
                              <td className="px-4 py-2.5 text-sm font-medium text-gray-700">
                                <div className="flex items-center gap-2">
                                  <svg
                                    className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                  {agentName(task.agent)}
                                </div>
                              </td>
                              <td className="px-4 py-2.5 text-sm text-gray-600">
                                Project #{task.project_id}
                              </td>
                              <td className="px-4 py-2.5 text-sm font-mono text-gray-600">
                                #{task.job_id}
                              </td>
                              <td className="px-4 py-2.5 text-xs text-gray-500">
                                {task.started_at ? new Date(task.started_at * 1000).toLocaleTimeString() : '-'}
                              </td>
                              <td className="px-4 py-2.5">
                                <span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
                                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></span>
                                  Running
                                </span>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr key={`${i}-details`}>
                                <td colSpan={5} className="px-4 py-4 bg-gray-50">
                                  <div className="space-y-2">
                                    <div className="flex items-center justify-between mb-3">
                                      <h4 className="text-sm font-semibold text-gray-900">
                                        Event History for Job #{task.job_id}
                                      </h4>
                                      <span className="text-xs text-gray-500">
                                        {jobEvents.length} events
                                      </span>
                                    </div>
                                    {jobEvents.length > 0 ? (
                                      <div className="max-h-80 overflow-y-auto space-y-2">
                                        {jobEvents.map((event, idx) => (
                                          <div key={idx} className="bg-white rounded-lg p-3 border border-gray-200 text-xs">
                                            <div className="flex items-start gap-2 mb-1">
                                              <span className="text-gray-400 font-mono whitespace-nowrap">
                                                {new Date(event.timestamp * 1000).toLocaleTimeString()}
                                              </span>
                                              <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${getLevelColor(event.level)}`}>
                                                {event.level}
                                              </span>
                                              <span className="font-medium text-gray-700">
                                                {agentName(event.agent)}
                                              </span>
                                            </div>
                                            <div className="text-gray-700 ml-2 mb-1">
                                              {event.message}
                                            </div>
                                            {event.detail && (
                                              <div className="ml-2 mt-2 p-2 bg-gray-100 rounded text-[10px] font-mono">
                                                {event.detail.type && (
                                                  <div className="mb-1">
                                                    <span className="text-gray-500">Type:</span>{' '}
                                                    <span className="text-blue-600 font-semibold">{event.detail.type}</span>
                                                  </div>
                                                )}
                                                {event.detail.agent && event.detail.agent !== event.agent && (
                                                  <div className="mb-1">
                                                    <span className="text-gray-500">Target Agent:</span>{' '}
                                                    <span className="text-purple-600 font-semibold">{agentName(event.detail.agent)}</span>
                                                  </div>
                                                )}
                                                {event.detail.system_type && (
                                                  <div className="mb-1">
                                                    <span className="text-gray-500">System:</span>{' '}
                                                    <span className="text-green-600">{event.detail.system_type}</span>
                                                  </div>
                                                )}
                                                {Object.keys(event.detail).length > 0 && (
                                                  <details className="mt-1">
                                                    <summary className="text-gray-500 cursor-pointer hover:text-gray-700">
                                                      Full Details
                                                    </summary>
                                                    <pre className="mt-1 text-[9px] text-gray-600 overflow-x-auto">
                                                      {JSON.stringify(event.detail, null, 2)}
                                                    </pre>
                                                  </details>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="text-center py-8 text-gray-400 text-sm">
                                        No events captured yet for this job
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="px-4 py-12 text-center text-gray-400">
                    No agents currently running
                  </div>
                )}
              </div>
            </div>

            {/* Registered Agents */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-base font-semibold text-gray-900">Registered Agents</h3>
                <p className="text-xs text-gray-400 mt-0.5">All agents available in the system</p>
              </div>
              <div className="p-6">
                <div className="flex flex-wrap gap-2">
                  {registeredAgents.map((agent: any) => (
                    <div
                      key={agent.system_type}
                      className={`px-3 py-2 rounded-lg border text-sm font-medium ${getAgentStatusColor(agent.system_type)}`}
                    >
                      {agentName(agent.system_type)}
                    </div>
                  ))}
                  {registeredAgents.length === 0 && (
                    <p className="text-gray-400 text-sm">No agents registered</p>
                  )}
                </div>
              </div>
            </div>

            {/* Real-time Event Stream */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Live Event Stream</h3>
                  <p className="text-xs text-gray-400 mt-0.5">Real-time agent events from all projects</p>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={autoScroll}
                      onChange={(e) => setAutoScroll(e.target.checked)}
                      className="rounded"
                    />
                    Auto-scroll
                  </label>
                  <button
                    onClick={() => setEvents([])}
                    className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="max-h-[600px] overflow-y-auto font-mono text-xs">
                {events.length > 0 ? (
                  <div className="divide-y divide-gray-50">
                    {events.map((event, i) => (
                      <div
                        key={i}
                        className={`px-4 py-2 hover:bg-gray-50 ${getLevelColor(event.level)} bg-opacity-30`}
                      >
                        <div className="flex items-start gap-3">
                          <span className="text-gray-400 whitespace-nowrap">
                            {new Date(event.timestamp * 1000).toLocaleTimeString()}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${getLevelColor(event.level)}`}>
                            {event.level}
                          </span>
                          <span className="font-medium text-gray-700">
                            {agentName(event.agent)}
                          </span>
                          <span className="text-gray-400">
                            Project #{event.project_id}
                            {event.job_id && ` · Job #${event.job_id}`}
                          </span>
                        </div>
                        <div className="mt-1 ml-32 text-gray-700">
                          {event.message}
                        </div>
                        {event.detail && (
                          <div className="mt-1 ml-32 text-gray-500 text-[10px]">
                            {JSON.stringify(event.detail)}
                          </div>
                        )}
                      </div>
                    ))}
                    <div ref={eventsEndRef} />
                  </div>
                ) : (
                  <div className="px-4 py-12 text-center text-gray-400">
                    {isConnected ? 'Waiting for events...' : 'Not connected to event stream'}
                  </div>
                )}
              </div>
            </div>

            {/* Agent Stats */}
            {stats && stats.events_by_agent && stats.events_by_agent.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-base font-semibold text-gray-900">Agent Event Statistics</h3>
                  <p className="text-xs text-gray-400 mt-0.5">Total events logged per agent</p>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    {stats.events_by_agent.slice(0, 15).map((a: any) => {
                      const maxCount = stats.events_by_agent[0]?.count || 1;
                      const percentage = (a.count / maxCount) * 100;
                      return (
                        <div key={a.agent}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="font-medium text-gray-700">{agentName(a.agent)}</span>
                            <span className="text-gray-500">{a.count.toLocaleString()} events</span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-brand-500 rounded-full transition-all"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  const colorMap: Record<string, string> = {
    brand: 'bg-brand-50 border-brand-200 text-brand-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
  };
  return (
    <div className={`rounded-xl border px-4 py-3 ${colorMap[color] || colorMap.brand}`}>
      <p className="text-xs opacity-70 mb-1">{label}</p>
      <p className="text-2xl font-bold">{value.toLocaleString()}</p>
    </div>
  );
}
